import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Phone, ExternalLink } from "lucide-react";
import { trackEvent } from "@/hooks/useBusinessData";

export default function BookingBlock({ config }) {
  const [showEmbed, setShowEmbed] = useState(false);

  // Load Calendly script lazily when user opens the embed
  useEffect(() => {
    if (!showEmbed) return;
    if (document.getElementById("calendly-css")) return;
    const link = document.createElement("link");
    link.id = "calendly-css";
    link.rel = "stylesheet";
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(link);
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
  }, [showEmbed]);

  return (
    <section
      id="book"
      className="relative bg-black text-white py-24 md:py-40 border-t border-white/10 overflow-hidden"
      data-testid="booking-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-6">
            <div className="eyebrow mb-6">§ Book Your Appointment</div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="font-serif text-4xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight"
            >
              Reserve the chair.
            </motion.h2>
            <p className="mt-6 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              Choose your service and find a time that works for you. Booking is powered by Calendly — your appointment is confirmed instantly.
            </p>
          </div>
          <div className="col-span-12 md:col-span-6 md:pl-8 flex flex-col justify-end">
            <div className="border border-white/10 p-6">
              <div className="eyebrow text-zinc-500 mb-2">Prefer to book by phone?</div>
              <a
                href={`tel:${config.phone_tel}`}
                onClick={() => trackEvent("call_clicked", { source: "book_block" })}
                className="font-serif text-3xl md:text-4xl line-hover inline-flex items-center gap-3"
                data-testid="book-phone-link"
              >
                <Phone className="w-6 h-6" strokeWidth={1.2} />
                {config.phone}
              </a>
            </div>
          </div>
        </div>

        {!showEmbed ? (
          <div className="border border-white/10 p-8 md:p-14 text-center bg-gradient-to-b from-white/[0.02] to-transparent">
            <Calendar className="w-10 h-10 mx-auto text-white/60" strokeWidth={1.2} />
            <div className="font-serif text-3xl md:text-4xl mt-6">
              Open the scheduling window
            </div>
            <p className="text-zinc-400 mt-3 max-w-md mx-auto">
              Real-time availability from Calendly. Select a service, date, and time — you'll receive an instant confirmation.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <button
                onClick={() => {
                  setShowEmbed(true);
                  trackEvent("calendly_opened", { source: "book_inline" });
                }}
                className="btn-primary"
                data-testid="book-open-calendly"
              >
                Open Calendar
              </button>
              <a
                href={config.calendly_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("calendly_opened", { source: "book_newtab" })}
                className="btn-ghost"
                data-testid="book-calendly-newtab"
              >
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                Open in New Tab
              </a>
            </div>
          </div>
        ) : (
          <div className="border border-white/10 bg-white overflow-hidden" data-testid="calendly-embed">
            <div
              className="calendly-inline-widget"
              data-url={`${config.calendly_url}?background_color=ffffff&text_color=000000&primary_color=000000`}
              style={{ minWidth: "320px", height: "760px" }}
            />
          </div>
        )}
      </div>
    </section>
  );
}
